/**
 * Admin payroll: run a month, review what it produced, drill into a payslip.
 *
 * Two things this screen refuses to hide.
 *
 * Employees the run could not pay are shown as prominently as the ones it
 * could. A run that quietly pays 19 of 20 people looks identical to a
 * successful one unless the 20th is put on screen.
 *
 * Every payslip line shows the arithmetic that produced it, not just an
 * amount. A deduction someone cannot see derived is a deduction they cannot
 * challenge.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  Banknote,
  CheckCircle2,
  Download,
  Loader2,
  Lock,
  Play,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import { money } from '../../lib/money';
import type {
  EmployeeLop,
  LopPreview,
  Page,
  Payslip,
  PayrollRun,
  PayrollRunDetail,
  PayrollRunResult,
  PayrollRunStatus,
  SkippedEmployee,
} from '../../types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_TONE: Record<PayrollRunStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  calculated: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function PayrollPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [openPayslipId, setOpenPayslipId] = useState<string | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<PayrollRunResult | null>(null);
  /** The run awaiting a cancellation reason / a payment reference. */
  const [cancelling, setCancelling] = useState<PayrollRun | null>(null);
  const [paying, setPaying] = useState<PayrollRun | null>(null);

  const queryClient = useQueryClient();

  const runs = useQuery<Page<PayrollRun>>({
    queryKey: ['payroll-runs'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/payroll/runs', { params: { limit: 50 } })).data,
  });

  /**
   * Loss of pay, keyed by employee id.
   *
   * Seeded from approved unpaid leave and then editable, because the derived
   * figure is advisory: the run takes LOP as an explicit input so that nothing
   * is ever docked from someone's pay without an administrator having seen the
   * number first.
   */
  const [lop, setLop] = useState<Record<string, string>>({});
  const [lopTouched, setLopTouched] = useState(false);

  const lopPreview = useQuery<LopPreview>({
    queryKey: ['lop-preview', year, month],
    queryFn: async () =>
      (await client.get('/api/v1/leave/lop-preview', { params: { year, month } })).data,
  });

  // Re-seed when the period changes, but never overwrite edits already made
  // for the period on screen.
  useEffect(() => {
    setLop({});
    setLopTouched(false);
  }, [year, month]);

  const derived = lopPreview.data?.employees ?? [];
  const effectiveLop = (employeeId: string, fallback: string) =>
    lop[employeeId] ?? fallback;

  const runPayroll = useMutation({
    mutationFn: async () => {
      // Only ids the API will recognise, and only values that actually dock
      // pay. The run refuses an id it does not know rather than silently
      // paying that person in full, so this must not invent keys.
      const payload: Record<string, number> = {};
      for (const entry of derived) {
        const value = Number(effectiveLop(entry.employeeId, entry.lopDays));
        if (value > 0) payload[entry.employeeId] = value;
      }
      return (await client.post('/api/v1/admin/payroll/run', {
        year,
        month,
        lopDays: Object.keys(payload).length ? payload : null,
      })).data as PayrollRunResult;
    },
    onSuccess: (result) => {
      setLastResult(result);
      void queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      toast.success(
        `${MONTHS[month - 1]} ${year}: ${result.run.employeeCount} payslip(s) calculated`,
      );
    },
    onError: (error) => toast.error(errorMessage(error, 'Payroll run failed')),
  });

  const approve = useMutation({
    mutationFn: async (runId: string) =>
      (await client.post(`/api/v1/admin/payroll/runs/${runId}/approve`)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-run'] });
      toast.success('Run approved — payslips are now visible to employees');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not approve')),
  });

  const cancel = useMutation({
    mutationFn: async ({ runId, reason }: { runId: string; reason: string }) =>
      (await client.post(`/api/v1/admin/payroll/runs/${runId}/cancel`, { reason })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-run'] });
      setCancelling(null);
      toast.success('Run cancelled — the period is open again');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not cancel')),
  });

  const markPaid = useMutation({
    mutationFn: async ({ runId, reference }: { runId: string; reference: string }) =>
      (await client.post(`/api/v1/admin/payroll/runs/${runId}/paid`, {
        reference: reference.trim() || null,
      })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-run'] });
      setPaying(null);
      toast.success('Run marked paid');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not mark as paid')),
  });

  return (
    <div className="space-y-6">
      <section className="card">
        <h2 className="text-sm font-semibold text-gray-900">Run payroll</h2>
        <p className="mt-1 text-xs text-gray-500">
          Re-running a month recalculates it in place. Approving freezes it —
          cancel the run to reopen the period. Marking a run paid is final: it
          can then be neither recalculated nor cancelled.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-gray-600">
            Month
            <select
              className="input mt-1 block"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((label, index) => (
                <option key={label} value={index + 1}>{label}</option>
              ))}
            </select>
          </label>

          <label className="text-xs font-medium text-gray-600">
            Year
            <input
              type="number"
              className="input mt-1 block w-28"
              value={year}
              min={2000}
              max={2100}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>

          <button
            type="button"
            className="btn-primary"
            onClick={() => runPayroll.mutate()}
            disabled={runPayroll.isPending}
          >
            {runPayroll.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Run payroll
          </button>
        </div>

        <LopPanel
          loading={lopPreview.isLoading}
          entries={derived}
          value={lop}
          touched={lopTouched}
          onChange={(employeeId, days) => {
            setLopTouched(true);
            setLop((current) => ({ ...current, [employeeId]: days }));
          }}
        />
      </section>

      {lastResult && lastResult.skipped.length > 0 && (
        <SkippedPanel
          skipped={lastResult.skipped}
          onDismiss={() => setLastResult(null)}
        />
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">Payroll runs</h2>

        {runs.isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : runs.isError ? (
          <ErrorPanel onRetry={() => void runs.refetch()} />
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5 font-medium">Period</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Employees</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Gross</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Deductions</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Net</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Employer cost</th>
                  <th scope="col" className="px-4 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {runs.data?.items.map((run) => (
                  <tr key={run.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                      {MONTHS[run.month - 1]} {run.year}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`badge ${STATUS_TONE[run.status]}`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {run.employeeCount}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {money(run.totalGross)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {money(run.totalDeductions)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums font-medium text-gray-900">
                      {money(run.totalNet)}
                    </td>
                    <td
                      className="px-4 py-2.5 tabular-nums text-gray-500"
                      title="Employer contributions — cost to company, not deducted from net pay"
                    >
                      {money(run.totalEmployerCost)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setOpenRunId(run.id)}
                      >
                        View
                      </button>
                      {run.status === 'calculated' && (
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => approve.mutate(run.id)}
                          disabled={approve.isPending}
                          aria-label={`Approve ${MONTHS[run.month - 1]} ${run.year}`}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Approve
                        </button>
                      )}
                      {run.status === 'approved' && (
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => setPaying(run)}
                          aria-label={`Mark ${MONTHS[run.month - 1]} ${run.year} paid`}
                        >
                          <Banknote className="h-4 w-4" />
                          Mark paid
                        </button>
                      )}
                      {/* Cancelling is the only way back out of an approval,
                          so it stays available until the run is actually paid. */}
                      {(run.status === 'calculated' || run.status === 'approved') && (
                        <button
                          type="button"
                          className="btn-ghost text-red-700"
                          onClick={() => setCancelling(run)}
                          aria-label={`Cancel ${MONTHS[run.month - 1]} ${run.year}`}
                        >
                          <Ban className="h-4 w-4" />
                          Cancel
                        </button>
                      )}
                      {run.status === 'paid' && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400"
                          title="Paid — this period is final and cannot be recalculated or cancelled"
                        >
                          <Lock className="h-3.5 w-3.5" />
                          final
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {runs.data?.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                      No payroll has been run yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openRunId && (
        <RunDrawer
          runId={openRunId}
          onClose={() => setOpenRunId(null)}
          onOpenPayslip={(id) => setOpenPayslipId(id)}
        />
      )}

      {openPayslipId && (
        <PayslipDrawer
          payslipId={openPayslipId}
          onClose={() => setOpenPayslipId(null)}
        />
      )}

      {cancelling && (
        <ReasonDialog
          title={`Cancel ${MONTHS[cancelling.month - 1]} ${cancelling.year}`}
          confirmLabel="Cancel run"
          destructive
          required
          label="Why is this run being cancelled?"
          help={
            cancelling.status === 'approved'
              ? 'This reopens an approved period. The reason is recorded in the audit log — recalculating replaces the payslips, so this entry is the only lasting record of what was voided.'
              : 'The reason is recorded in the audit log.'
          }
          pending={cancel.isPending}
          onCancel={() => setCancelling(null)}
          onConfirm={(reason) => cancel.mutate({ runId: cancelling.id, reason })}
        />
      )}

      {paying && (
        <ReasonDialog
          title={`Mark ${MONTHS[paying.month - 1]} ${paying.year} paid`}
          confirmLabel="Mark paid"
          label="Payment reference (optional)"
          help={`This records that ${money(paying.totalNet)} has actually been disbursed. It is final — a paid run cannot be recalculated or cancelled.`}
          pending={markPaid.isPending}
          onCancel={() => setPaying(null)}
          onConfirm={(reference) => markPaid.mutate({ runId: paying.id, reference })}
        />
      )}
    </div>
  );
}

/**
 * Loss of pay for the selected month.
 *
 * Shown before the run, not after, because this is the input that decides how
 * much someone loses. Derived from approved unpaid leave only — attendance
 * availability deliberately does not feed pay (HARDENING.md #6) — and every
 * figure stays editable, so the derivation is a starting point rather than a
 * verdict.
 */
function LopPanel({
  loading,
  entries,
  value,
  touched,
  onChange,
}: {
  loading: boolean;
  entries: EmployeeLop[];
  value: Record<string, string>;
  touched: boolean;
  onChange: (employeeId: string, days: string) => void;
}) {
  if (loading) return null;

  if (entries.length === 0) {
    return (
      <p className="mt-3 text-xs text-gray-500">
        No approved unpaid leave this month — everyone is paid a full month
        unless you record loss of pay against them.
      </p>
    );
  }

  const anyApproximate = entries.some((e) => e.approximate);

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
      <h3 className="text-xs font-semibold text-amber-900">
        Loss of pay ({entries.length})
      </h3>
      <p className="mt-0.5 text-xs text-amber-800">
        Derived from approved unpaid leave. Edit anything that looks wrong —
        these are the days that will be docked.
      </p>

      <ul className="mt-2 space-y-2">
        {entries.map((entry) => (
          <li key={entry.employeeId} className="flex flex-wrap items-center gap-2">
            <span className="min-w-40 text-sm text-gray-900">
              {entry.employeeName ?? entry.employeeCode}
              <span className="ml-1.5 text-xs text-gray-500">
                {entry.employeeCode}
              </span>
            </span>
            <input
              type="number"
              step={0.5}
              min={0}
              max={31}
              className="input w-24"
              aria-label={`Loss of pay days for ${entry.employeeCode}`}
              value={value[entry.employeeId] ?? entry.lopDays}
              onChange={(e) => onChange(entry.employeeId, e.target.value)}
            />
            <span className="text-xs text-gray-500">days</span>
            {entry.approximate && (
              <span
                className="inline-flex items-center gap-1 text-xs text-amber-800"
                title="This leave crosses the month boundary, so the split between months is an estimate"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                check
              </span>
            )}
            {entry.note && (
              <span className="w-full text-xs text-gray-500">{entry.note}</span>
            )}
          </li>
        ))}
      </ul>

      {anyApproximate && (
        <p className="mt-2 text-xs text-amber-800">
          Entries marked <strong>check</strong> come from leave spanning two
          months. A leave request stores a total, not which specific days were
          half days, so the split between months is apportioned — confirm it
          before running.
        </p>
      )}
      {touched && (
        <p className="mt-2 text-xs text-gray-600">
          Edited. The run will use the values shown here, not the derived ones.
        </p>
      )}
    </div>
  );
}

/**
 * A confirm dialog that collects one line of text.
 *
 * Deliberately not `window.prompt`: a native dialog blocks the event loop, is
 * unstyleable, and gives no way to show why the reason matters. Both of these
 * actions change the state of a payroll period, so the consequence is spelled
 * out on screen rather than assumed.
 */
function ReasonDialog({
  title,
  label,
  help,
  confirmLabel,
  pending,
  required = false,
  destructive = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  label: string;
  help: string;
  confirmLabel: string;
  pending: boolean;
  required?: boolean;
  destructive?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const tooShort = required && value.trim().length < 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
      >
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-xs text-gray-500">{help}</p>

        <label className="mt-3 block text-xs font-medium text-gray-600">
          {label}
          <textarea
            spellCheck={true}
            className="input mt-1 block w-full"
            rows={3}
            value={value}
            autoFocus
            onChange={(e) => setValue(e.target.value)}
          />
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Back
          </button>
          <button
            type="button"
            className={destructive ? 'btn-primary bg-red-600 hover:bg-red-700' : 'btn-primary'}
            disabled={pending || tooShort}
            onClick={() => onConfirm(value)}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorPanel({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="card flex items-center justify-between border border-red-200 bg-red-50">
      <p className="text-sm text-red-800">
        Could not load payroll. This is a failure to fetch, not an empty list.
      </p>
      <button type="button" className="btn-ghost" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function SkippedPanel({
  skipped,
  onDismiss,
}: {
  skipped: SkippedEmployee[];
  onDismiss: () => void;
}) {
  return (
    <section className="card border border-amber-200 bg-amber-50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <h2 className="text-sm font-semibold text-amber-900">
              {skipped.length} employee{skipped.length === 1 ? '' : 's'} were not paid
            </h2>
            <p className="mt-0.5 text-xs text-amber-800">
              They were skipped, not failed. Each needs fixing before the next run.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-ghost"
          onClick={onDismiss}
          aria-label="Dismiss skipped employees"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ul className="mt-3 space-y-1 text-sm">
        {skipped.map((entry) => (
          <li key={entry.employeeId} className="flex flex-wrap gap-x-2 text-amber-900">
            <span className="font-medium">{entry.employeeCode}</span>
            <span className="text-amber-800">— {entry.reason}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RunDrawer({
  runId,
  onClose,
  onOpenPayslip,
}: {
  runId: string;
  onClose: () => void;
  onOpenPayslip: (id: string) => void;
}) {
  const run = useQuery<PayrollRunDetail>({
    queryKey: ['payroll-run', runId],
    queryFn: async () =>
      (await client.get(`/api/v1/admin/payroll/runs/${runId}`)).data,
  });

  return (
    <Drawer title="Payroll run" onClose={onClose}>
      {run.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      ) : run.isError ? (
        <p className="text-sm text-red-700">Could not load this run.</p>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            {MONTHS[(run.data?.month ?? 1) - 1]} {run.data?.year} ·{' '}
            <span className="font-medium">{run.data?.status}</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Policy version {run.data?.statutoryPolicyId ?? 'none'} — recorded so this
            run can be reproduced after rates change.
          </p>

          <table className="mt-4 w-full text-sm">
            <thead className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
              <tr>
                <th scope="col" className="py-2 font-medium">Employee</th>
                <th scope="col" className="py-2 font-medium">Days</th>
                <th scope="col" className="py-2 font-medium">Gross</th>
                <th scope="col" className="py-2 font-medium">Net</th>
                <th scope="col" className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {run.data?.payslips.map((slip) => (
                <tr key={slip.id}>
                  <td className="py-2 text-gray-700">
                    {slip.employeeName ?? slip.employeeCode}
                    {slip.employeeName && (
                      <span className="ml-1.5 text-xs text-gray-400">
                        {slip.employeeCode}
                      </span>
                    )}
                  </td>
                  <td className="py-2 tabular-nums text-gray-600">
                    {slip.payableDays}/{slip.totalDays}
                  </td>
                  <td className="py-2 tabular-nums text-gray-600">
                    {money(slip.grossEarnings)}
                  </td>
                  <td className="py-2 tabular-nums font-medium text-gray-900">
                    {money(slip.netPay)}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => onOpenPayslip(slip.id)}
                    >
                      Payslip
                    </button>
                  </td>
                </tr>
              ))}
              {run.data?.payslips.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-400">
                    This run produced no payslips.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </Drawer>
  );
}

export function PayslipDrawer({
  payslipId,
  onClose,
  selfService = false,
}: {
  payslipId: string;
  onClose: () => void;
  selfService?: boolean;
}) {
  const base = selfService ? '/api/v1/employees/me' : '/api/v1/admin/payroll';

  const payslip = useQuery<Payslip>({
    queryKey: ['payslip', base, payslipId],
    queryFn: async () => (await client.get(`${base}/payslips/${payslipId}`)).data,
  });

  const download = useMutation({
    mutationFn: async () => {
      const response = await client.get(`${base}/payslips/${payslipId}/pdf`, {
        responseType: 'blob',
      });
      return response.data as Blob;
    },
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      // Named from the payslip itself rather than read back from
      // Content-Disposition, which a cross-origin response does not expose
      // unless the header is allow-listed. Matches the server's filename.
      const slip = payslip.data;
      anchor.download = slip
        ? `payslip-${slip.employeeCode}-${slip.year}-${String(slip.month).padStart(2, '0')}.pdf`
        : 'payslip.pdf';
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not download the PDF')),
  });

  const lines = payslip.data?.lines ?? [];
  const section = (type: string) => lines.filter((l) => l.componentType === type);

  return (
    <Drawer title="Payslip" onClose={onClose}>
      {payslip.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      ) : payslip.isError ? (
        <p className="text-sm text-red-700">Could not load this payslip.</p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">
                {payslip.data
                  ? `${MONTHS[payslip.data.month - 1]} ${payslip.data.year}`
                  : ''}
              </span>
              {payslip.data?.employeeName && (
                <span className="text-gray-500"> · {payslip.data.employeeName}</span>
              )}
              <br />
              {payslip.data?.payableDays} of {payslip.data?.totalDays} days paid
              {Number(payslip.data?.lopDays) > 0 && (
                <span className="text-amber-700">
                  {' '}· {payslip.data?.lopDays} LOP
                </span>
              )}
            </p>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => download.mutate()}
              disabled={download.isPending}
            >
              {download.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              PDF
            </button>
          </div>

          <LineSection title="Earnings" lines={section('earning')} />
          <LineSection title="Deductions" lines={section('deduction')} />

          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
            <span className="text-sm font-semibold text-gray-900">Net pay</span>
            <span className="text-base font-semibold tabular-nums text-gray-900">
              {money(payslip.data?.netPay)}
            </span>
          </div>

          {section('employer_contribution').length > 0 && (
            <LineSection
              title="Employer contributions (not deducted from pay)"
              lines={section('employer_contribution')}
              muted
            />
          )}
        </>
      )}
    </Drawer>
  );
}

function LineSection({
  title,
  lines,
  muted = false,
}: {
  title: string;
  lines: Payslip['lines'];
  muted?: boolean;
}) {
  if (lines.length === 0) return null;
  return (
    <section className="mt-4">
      <h3 className={`text-xs font-semibold uppercase ${muted ? 'text-gray-400' : 'text-gray-500'}`}>
        {title}
      </h3>
      <ul className="mt-1.5 space-y-1.5">
        {lines.map((line) => (
          <li key={line.code}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-gray-800">{line.label}</span>
              <span className="text-sm tabular-nums text-gray-900">
                {money(line.amount)}
              </span>
            </div>
            {line.calculationNote && (
              <p className="text-xs text-gray-500">{line.calculationNote}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Drawer({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
